import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supplierService } from '../../services/supplierService';
import ConfirmModal from '../../components/Common/ConfirmModal';
import Toast from '../../components/Common/Toast';
import MutateGate from '../../components/Common/MutateGate';
import ViewOrEditLink from '../../components/Common/ViewOrEditLink';

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoadedSuppliers = useRef(false);

  useEffect(() => {
    if (!hasLoadedSuppliers.current) {
      hasLoadedSuppliers.current = true;
      loadSuppliers();
    }
  }, []);

  const loadSuppliers = async () => {
    setLoadingSuppliers(true);
    console.log('📡 [Suppliers] Cargando proveedores desde backend...');
    
    try {
      const response = await supplierService.getSuppliers({ entity_provider: true });
      
      if (response.success && response.data) {
        console.log('✅ [Suppliers] Proveedores cargados exitosamente:', response.data.length);
        setSuppliers(response.data);
      } else {
        console.warn('⚠️ [Suppliers] Respuesta sin éxito:', response);
        setToast({
          isOpen: true,
          message: 'Error al cargar los proveedores',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('❌ [Suppliers] Error al cargar proveedores:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar los proveedores',
        type: 'error'
      });
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const handleDeleteClick = (supplier) => {
    setSupplierToDelete(supplier);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!supplierToDelete) return;
    
    setLoading(true);
    console.log('🗑️ [Suppliers] Eliminando proveedor:', supplierToDelete.id);
    
    try {
      const response = await supplierService.deleteSupplier(supplierToDelete.id);
      
      if (response.success) {
        console.log('✅ [Suppliers] Proveedor eliminado exitosamente');
        await loadSuppliers();
        setIsDeleteModalOpen(false);
        setSupplierToDelete(null);
        setToast({
          isOpen: true,
          message: 'Proveedor eliminado exitosamente',
          type: 'success'
        });
      } else {
        throw new Error(response.message || 'Error al eliminar el proveedor');
      }
    } catch (error) {
      console.error('❌ [Suppliers] Error al eliminar proveedor:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar el proveedor',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Filtrar proveedores
  const filteredSuppliers = suppliers.filter(supplier => {
    const docNumber = supplier.document_number || '';
    const matchesSearch = !searchTerm || 
      (supplier.code && supplier.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (supplier.name && supplier.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (docNumber && docNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (supplier.email && supplier.email.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && supplier.is_active) ||
      (statusFilter === 'inactive' && !supplier.is_active);

    return matchesSearch && matchesStatus;
  });

  // Calcular estadísticas
  const totalSuppliers = suppliers.length;
  const activeSuppliers = suppliers.filter(s => s.is_active).length;
  const inactiveSuppliers = suppliers.filter(s => !s.is_active).length;

  return (
    <div>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Proveedores</h1>
                <p className="text-gray-600 mt-1">Gestión de proveedores</p>
              </div>
              <MutateGate>
                <Link
                  to="/suppliers/create"
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors inline-block"
                >
                  + Nuevo Proveedor
                </Link>
              </MutateGate>
            </div>

            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-4">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Proveedores</span>
            </nav>

            {/* Filtros y Búsqueda */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Filtro por Estado */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">Todos los Estados</option>
                  <option value="active">Activos</option>
                  <option value="inactive">Inactivos</option>
                </select>

                {/* Búsqueda */}
                <input
                  type="text"
                  placeholder="Buscar por código, nombre, documento o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            {/* Estadísticas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Total</div>
                <div className="text-2xl font-bold text-gray-800">{totalSuppliers}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Activos</div>
                <div className="text-2xl font-bold text-teal-600">{activeSuppliers}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Inactivos</div>
                <div className="text-2xl font-bold text-gray-500">{inactiveSuppliers}</div>
              </div>
            </div>
          </div>

          {/* Tabla de Proveedores */}
          {loadingSuppliers ? (
            <div className="text-center py-12">
              <div className="text-gray-500">Cargando proveedores...</div>
            </div>
          ) : filteredSuppliers.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">No se encontraron proveedores</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Código
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Documento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contacto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Entidades
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSuppliers.map((supplier) => (
                    <tr key={supplier.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/suppliers/${supplier.id}/edit`}
                          className="text-sm font-mono text-teal-600 hover:text-teal-900"
                        >
                          {supplier.code}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{supplier.name}</div>
                        {supplier.contact_name && (
                          <div className="text-xs text-gray-500">Contacto: {supplier.contact_name}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {supplier.document_type === 'dni' ? 'DNI' : supplier.document_type === 'other' ? 'Otro' : 'RUC'}: {supplier.document_number || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{supplier.email || '-'}</div>
                        {supplier.phone ? (
                          <div className="text-xs text-gray-500">{supplier.phone}</div>
                        ) : (
                          <div className="text-xs text-gray-500">-</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {supplier.is_provider ? (
                            <span className="px-2 py-1 text-xs rounded-full bg-teal-100 text-teal-800">Proveedor</span>
                          ) : null}
                          {supplier.is_client ? (
                            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">Cliente</span>
                          ) : null}
                          {supplier.is_internal ? (
                            <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800">Interno</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          supplier.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {supplier.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <ViewOrEditLink
                          to={`/suppliers/${supplier.id}/edit`}
                          className="text-purple-600 hover:text-purple-900 mr-4"
                        />
                        <MutateGate>
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(supplier)}
                            className="text-red-600 hover:text-red-900"
                            title="Eliminar"
                          >
                            Eliminar
                          </button>
                        </MutateGate>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmación de eliminación */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSupplierToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Proveedor"
        message={`¿Estás seguro de que deseas eliminar el proveedor "${supplierToDelete?.name}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        isLoading={loading}
      />

      {/* Toast de notificaciones */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default Suppliers;

